import CategoryDetailAdvancedSeoCategories from './CategoryDetailAdvancedSeoCategories/CategoryDetailAdvancedSeoCategories';
import { CategoryDetailProductsWrapper } from './CategoryDetailProductsWrapper';
import { CategoryHeader } from './CategoryHeader';
import Overlay from 'components/Basic/Overlay/Overlay';
import Adverts from 'components/Blocks/Adverts/Adverts';
import CategorySubcategories from 'components/Blocks/Categories/CategorySubcategories/CategorySubcategories';
import { CategoryPromotedProducts } from 'components/Blocks/Product/CategoryPromotedProducts/CategoryPromotedProducts';
import { FilterPanel } from 'components/Blocks/Product/Filter/FilterPanel/FilterPanel';
import { SelectedFilters } from 'components/Blocks/Product/Filter/SelectedParameters/SelectedFilters';
import SortingBar from 'components/Blocks/SortingBar/SortingBar';
import Webline from 'components/Layout/Webline/Webline';
import { useCurrentPageQuery } from 'helpers/queryParams/useCurrentPageQuery';
import { useSeoTitleWithPagination } from 'helpers/seo/useSeoTitleWithPagination';
import { usePersoo } from 'hooks/persoo/usePersoo';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { PERSOO_IDS } from 'modules/persoo/persooTypes';
import { FilterIcon } from 'public/svg/IconsSvg';
import React, { useCallback, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { CategoryDetailType } from 'types/category';

type CategoryDetailProps = {
    category: CategoryDetailType;
};

export const getCategoryPanelWrapperTwClass = (isOpen: boolean): string => {
    return twJoin(
        'fixed top-0 left-0 bottom-0 right-10 max-w-[400px] -translate-x-full vl:static vl:w-[304px] vl:translate-x-0 vl:transition-none',
        isOpen && 'z-aboveOverlay transition-all duration-200 ease-defaultTransition translate-x-0',
    );
};
export const categoryDetailContentTwClass = 'flex flex-1 flex-col vl:pl-6';
export const categoryDetailPanelOpenerTwClass =
    'relative mr-1 mb-1 flex min-h-[50px] cursor-pointer flex-row items-center justify-center whitespace-nowrap rounded-md bg-primary px-4 text-body font-normal text-white vl:hidden';
export const categoryDetailSortingPanelTwClass = 'mb-8 flex w-full flex-wrap items-center justify-between vl:mb-0';
export const categoryDetailSortingWrapperTwClass = 'flex flex-col sm:block';
export const categoryDetailPanelIconTwClass = 'mr-1 h-4 w-4 flex-shrink-0 font-bold text-white';

const CategoryDetail: FC<CategoryDetailProps> = ({ category }) => {
    const t = useTypedTranslationFunction();
    const { isPersooAllowed } = usePersoo();
    const title = useSeoTitleWithPagination(category.productConnection.totalCount, category.name, category.seoH1);

    const { type: domainType } = useDomainConfig();
    const currentPage = useCurrentPageQuery();

    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const paginationScrollTargetRef = useRef<HTMLDivElement>(null);

    const handlePanelOpenerClick = useCallback(() => {
        const body = document.getElementsByTagName('body')[0];

        setIsPanelOpen((prev) => {
            body.style.overflow = prev ? 'visible' : 'hidden';

            return !prev;
        });
    }, []);
    const filterOptions = category.productConnection.productFilterOptions;

    return (
        <Webline>
            <CategoryHeader
                title={title}
                description={category.shortDescription}
                slug={category.slug}
                uuid={category.uuid}
                imageSrc={category.image}
                name={category.name}
                currentPage={currentPage}
                adverts={<Adverts positionName="productList" className="mb-4" />}
            />
            <Adverts positionName="productListMiddle" currentCategory={category} className="mb-4" />
            {!category.isRoot && (
                <CategorySubcategories listedItems={[...category.children, ...category.linkedCategories]} />
            )}
            <div className="relative mb-8 flex flex-col vl:mb-10 vl:flex-row">
                {filterOptions !== null && (
                    <>
                        <div className={getCategoryPanelWrapperTwClass(isPanelOpen)}>
                            <FilterPanel
                                panelCloseHandler={handlePanelOpenerClick}
                                totalCount={category.productConnection.totalCount}
                                productFilterOptions={filterOptions}
                            />
                        </div>
                        {isPanelOpen && <Overlay isHiddenOnDesktop onClick={handlePanelOpenerClick} />}
                        <div className={categoryDetailContentTwClass}>
                            {!category.isRoot && <div id={isPersooAllowed ? PERSOO_IDS.categoryProducts : undefined} />}
                            <Adverts className="mb-4" positionName="productList" />
                            <CategoryDetailAdvancedSeoCategories
                                readyCategorySeoMixLinks={category.readyCategorySeoMixLinks}
                            />
                            {!category.isRoot && domainType !== 'web' && (
                                <CategoryPromotedProducts items={category.bestsellers} />
                            )}
                            <div className={categoryDetailSortingWrapperTwClass} ref={paginationScrollTargetRef}>
                                <SelectedFilters filterOptions={filterOptions} />
                                <div className={categoryDetailSortingPanelTwClass}>
                                    <div
                                        className={categoryDetailPanelOpenerTwClass}
                                        id="js-category-detail-panel"
                                        onClick={handlePanelOpenerClick}
                                    >
                                        <FilterIcon className={categoryDetailPanelIconTwClass} />
                                        {t('Product filter')}
                                    </div>
                                    <SortingBar
                                        sorting={category.productConnection.orderingMode}
                                        totalCount={category.productConnection.totalCount}
                                    />
                                </div>
                            </div>
                            <CategoryDetailProductsWrapper
                                category={category}
                                paginationScrollTargetRef={paginationScrollTargetRef}
                            />
                        </div>
                    </>
                )}
            </div>
        </Webline>
    );
};

export default CategoryDetail;
